"use client";

import * as React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getTextWidth } from "senf-shared/utils/helpers";
import { tv } from "tailwind-variants";
import { Icon, Shake } from "../../..";
import { cn } from "../../../util/classNameConstructor";
import type { IconName } from "../icon/names";
import TooltipProvider, {
  type TooltipProviderProps,
} from "../tooltip/TooltipProvider";
import CharacterCounter from "./CharacterCounter";

/**
 * Automatically resizes a textarea between minRows and maxRows.
 * Uses computed line-height and respects box-sizing.
 */
export function autoResizeTextarea(
  textarea: HTMLTextAreaElement,
  options: {
    minRows?: number;
    maxRows?: number;
  } = {},
): void {
  if (!textarea) return;

  const computedStyle = window.getComputedStyle(textarea);
  const borderBox = computedStyle.boxSizing === "border-box";
  const lineHeight = Number.parseFloat(computedStyle.lineHeight);

  if (!lineHeight || Number.isNaN(lineHeight)) return;

  const paddingOffset = borderBox
    ? Number.parseFloat(computedStyle.borderTopWidth || "0") +
      Number.parseFloat(computedStyle.borderBottomWidth || "0") +
      Number.parseFloat(computedStyle.paddingTop || "0") +
      Number.parseFloat(computedStyle.paddingBottom || "0")
    : 0;

  const minRows =
    options.minRows ??
    Number.parseInt(textarea.getAttribute("rows") || "1", 10);
  const maxRows = options.maxRows;

  const minHeight = Number(minRows) * lineHeight + paddingOffset;
  const maxHeight = maxRows
    ? Number(maxRows) * lineHeight + paddingOffset
    : Number.POSITIVE_INFINITY;

  textarea.style.height = "auto"; // Reset to shrink if needed
  const contentHeight = textarea.scrollHeight;

  const newHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
  textarea.style.height = `${newHeight}px`;
  textarea.style.minHeight = `${0}px`;
}
const inputVariants = tv({
  slots: {
    container: ["gap-y-1 flex flex-col w-full"],
    inputContainer: [
      "relative cursor-text flex items-center rounded-xl w-full px-3 bg-surface-subdued duration-100 outline-none gap-2",
      "ring-inset ring-1 ring-border-subdued has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-focused-default",
      "disabled:opacity-50",
      // Autofill styles moved to container
      "has-[input:-webkit-autofill]:bg-[var(--palette-yellow-100)] has-[input:-webkit-autofill]:ring-[var(--palette-yellow-400)]",
      "hover:has-[input:-webkit-autofill]:bg-[var(--palette-yellow-100)] hover:has-[input:-webkit-autofill]:ring-[var(--palette-yellow-400)]",
      "has-[:focus-visible]:has-[input:-webkit-autofill]:bg-[var(--palette-yellow-100)] has-[:focus-visible]:has-[input:-webkit-autofill]:ring-[var(--palette-yellow-400)]",
      "[--border-width:1px] has-[:focus-visible]:[--border-width:2px]",
    ],
    inputWrapper: [
      "relative flex w-full gap-1 items-baseline self-start mt-px",
    ],
    input: [
      "peer/input w-auto grow bg-transparent outline-none border-none p-0 min-w-0",
      "placeholder:text-text-subdued focus-visible:placeholder:text-text-placeholder",
      "disabled:opacity-50",
      // Hide browser default number input steppers
      "[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0",
      "[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0",
      "[&[type=number]]:[-moz-appearance:textfield]",
      // Autofill styles for text color only
      "autofill:[-webkit-text-fill-color:var(--text-default)]",
      "hover:autofill:[-webkit-text-fill-color:var(--text-default)]",
      "focus-visible:autofill:[-webkit-text-fill-color:var(--text-default)]",
    ],
    label: [
      "text-text-subdued absolute start-3 transition-all duration-500 ease-expressive-out pointer-events-none",
      "cursor-text select-none origin-left w-max truncate",
    ],
    leadingIcon: ["relative text-text-subdued pointer-events-none shrink-0"],
    trailingIcon: ["-mx-1 p-1 text-icon-default rounded-2xl shrink-0"],
    stepper: [
      "group/stepper absolute flex flex-col justify-center border-l text-border-subdued border-current",
      "end-[var(--border-width)] top-[var(--border-width)] bottom-[var(--border-width)] duration-100",
    ],
    stepperButton: [
      "flex items-center justify-center w-8 pe-px me-[calc(2px-var(--border-width))] h-5 text-text-subdued group-hover/stepper:text-text-placeholder hover:!text-text-default duration-100 shrink-0",
      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focused-default",
      "disabled:opacity-50 disabled:cursor-not-allowed",
    ],
    stepperDivider: ["border-b border-solid border-current"],
    helperText: ["text-xs text-text-subdued"],
  },
  variants: {
    size: {
      md: {
        inputContainer: "min-h-9 text-sm",
        inputWrapper: "py-2",
      },
      lg: {
        inputContainer: "min-h-11 text-base",
        inputWrapper: "py-2.5",
        input: "my-px",
      },
      xl: {
        inputContainer: "min-h-14 text-base",
        inputWrapper: "py-2.5",
        input: "my-px",
      },
    },
    hasLabel: {
      true: {
        label: "top-1",
      },
      false: {
        input: "",
        label: "top-1/2 -translate-y-1/2",
      },
    },
    labelOutside: {
      true: {
        label:
          "static text-sm !translate-y-0 !text-text-default pointer-events-auto cursor-default",
      },
      false: {
        label:
          "absolute start-3 transition-all duration-200 ease-expressive-out pointer-events-none cursor-text select-none origin-left",
      },
    },
    hasError: {
      true: {
        inputContainer:
          "ring-border-critical-default has-[:focus-visible]:ring-border-critical-default",
        label: "text-text-critical",
        helperText: "text-text-critical",
      },
      false: {
        input: "",
        helperText: "",
      },
    },
    hasSuccess: {
      true: {
        inputContainer:
          "ring-border-success-default has-[:focus-visible]:ring-border-success-default",
        label: "text-text-success",
        helperText: "text-text-success",
      },
      false: {
        helperText: "",
      },
    },
    hasLeadingIcon: {
      true: {
        input: "",
        label: "start-10 [--leading-icon-offset:28px]",
      },
      false: {
        input: "",
      },
    },
    hasTrailingIcon: {
      true: {
        label: "end-10 [--trailing-icon-offset:28px]",
      },
      false: {},
    },
    hasStepper: {
      true: {
        input: "pe-8 grow",
        label: "end-8",
      },
      false: {},
    },
    isFloating: {
      true: {
        label:
          "scale-[var(--scaled)] text-text-subdued max-w-[calc((100%-var(--leading-icon-offset,0px)-var(--trailing-icon-offset,0px)-1.5rem)/var(--scaled))]",
      },
      false: {
        label: [
          "top-1/2 -translate-y-1/2 text-text-subdued peer-autofill/input:top-1 peer-autofill/input:translate-y-0 peer-autofill/input:scale-[var(--scaled)] peer-autofill/input:text-text-subdued",
          "max-w-[calc(100%-var(--leading-icon-offset,0px)-var(--trailing-icon-offset,0px)-1.5rem)]",
        ],
      },
    },
    isTextarea: {
      true: {
        inputContainer: "h-auto items-start",
        input: "min-h-[6rem] pt-2 pb-0 resize-none block leading-snug",
      },
      false: {
        input: "leading-none",
      },
    },
    trailingIconClickable: {
      true: {
        trailingIcon:
          "cursor-pointer hover:text-text-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focused-black",
      },
      false: {
        trailingIcon: "pointer-events-none",
      },
    },
  },
  compoundVariants: [
    // Size + hasLabel combinations for input padding-top (only when label is inside)
    {
      size: "md",
      hasLabel: true,
      labelOutside: false,
      isTextarea: false,
      class: {
        inputWrapper: "pt-3 pb-0 mt-1",
      },
    },
    {
      size: "lg",
      hasLabel: true,
      labelOutside: false,
      isTextarea: false,
      class: {
        inputWrapper: "pt-4 pb-1 mt-1",
      },
    },
    {
      size: "xl",
      hasLabel: true,
      labelOutside: false,
      isTextarea: false,
      class: {
        inputWrapper: "pt-5 pb-1 mt-1.5",
      },
    },
    // Floating label positioning for different sizes (only when label is inside)
    {
      size: "md",
      isFloating: true,
      labelOutside: false,
      class: {
        label: "top-0.5",
      },
    },
    {
      size: "lg",
      isFloating: true,
      labelOutside: false,
      class: {
        label: "top-1",
      },
    },
    {
      size: "xl",
      isFloating: true,
      labelOutside: false,
      class: {
        label: "top-2",
      },
    },
    // Outside label positioning adjustments for leading icons
    {
      labelOutside: true,
      hasLeadingIcon: true,
      class: {
        label: "static text-text-default text-sm translate-y-0",
      },
    },
    // Textarea specific styles
    {
      isTextarea: true,
      hasLabel: false,
      class: {
        input: "pt-0 mt-0",
      },
    },
    {
      isTextarea: true,
      hasLabel: true,
      isFloating: true,
      labelOutside: false,
      class: {
        label:
          "top-1 scale-[var(--scaled)] text-text-subdued bg-surface-subdued",
        input: "pt-3 mt-0",
      },
    },
    {
      isTextarea: true,
      hasLabel: true,
      isFloating: false,
      labelOutside: false,
      class: {
        label: "top-5 text-text-subdued",
        input: "pt-3 mt-0",
      },
    },
    // Textarea with outside label
    {
      isTextarea: true,
      labelOutside: true,
      class: {
        label: "static text-text-default text-sm translate-y-0",
        input: "py-0",
      },
    },
    // Stepper height constraints for different input sizes
  ],
  defaultVariants: {
    size: "lg",
    hasError: false,
    hasSuccess: false,
    hasLeadingIcon: false,
    hasTrailingIcon: false,
    hasStepper: false,
    isTextarea: false,
    trailingIconClickable: false,
    labelOutside: false,
  },
});

// Generic interface that supports both string and number values
interface BaseInputProps {
  value: string | undefined;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  label?: string;
  helper?: string | boolean;
  error?: string | boolean;
  success?: string | boolean;
  leadingIcon?: IconName | React.ReactNode;
  leadingIconProps?: React.HTMLAttributes<HTMLDivElement>;
  trailingIcon?: IconName | React.ReactNode;
  trailingIconProps?: React.HTMLAttributes<HTMLDivElement> & {
    label?: TooltipProviderProps["primaryContent"];
  };
  rows?: number;
  maxRows?: number;
  size?: "md" | "lg" | "xl";
  labelOutside?: boolean;
  validate?: boolean | ((value: string | undefined) => Promise<boolean>);
  prepend?: React.ReactNode;
  append?: React.ReactNode;
  classNames?: {
    input?: string;
    label?: string;
    container?: string;
    inputContainer?: string;
    inputWrapper?: string;
    leadingIcon?: string;
    trailingIcon?: string;
  };
}

// String input props
interface StringInputProps
  extends BaseInputProps,
    Omit<
      React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
      "size" | "value" | "onChange"
    > {
  type?: Exclude<React.HTMLInputTypeAttribute, "number"> | "textarea";
  step?: never;
  min?: never;
  max?: never;
}

// Number input props
interface NumberInputProps
  extends BaseInputProps,
    Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      "size" | "value" | "onChange" | "type"
    > {
  type: "number";
  step?: number;
  min?: number;
  max?: number;
  rows?: never;
  maxRows?: never;
  inputMode?: "decimal" | "numeric";
  pattern?: string;
}

export type InputProps = StringInputProps | NumberInputProps;

export const Input = React.forwardRef<
  Omit<HTMLInputElement | HTMLTextAreaElement, "size">,
  InputProps
>(
  (
    {
      label,
      helper,
      error,
      success,
      leadingIcon,
      leadingIconProps,
      trailingIcon,
      trailingIconProps,
      className,
      classNames,
      value,
      onChange,
      onFocus,
      onBlur,
      disabled,
      required,
      type = "text",
      id,
      placeholder,
      minLength,
      maxLength,
      rows = 3,
      maxRows = 10,
      size = "lg",
      labelOutside,
      validate,
      prepend,
      append,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const internalInputRef = React.useRef<
      HTMLInputElement | HTMLTextAreaElement
    >(null);
    const inputId = React.useId();
    const [isFocused, setIsFocused] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(Boolean(value));
    const [showPassword, setShowPassword] = React.useState(false);
    const [triggerErrorShake, setTriggerErrorShake] = React.useState(false);

    const isTextarea = type === "textarea";
    const isNumberType = type === "number";

    React.useEffect(() => {
      setHasValue(Boolean(value));
    }, [value]);

    const handleSetFocus = (
      e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    ) => {
      // Don't focus if clicking on interactive elements
      const target = e.target as HTMLElement;
      const isInteractive =
        target.closest("button") ||
        target.closest('[role="button"]') ||
        target.closest("input") ||
        target.closest("textarea");

      if (!isInteractive && internalInputRef.current) {
        e.preventDefault();
        internalInputRef.current.focus();
      }
    };

    const handleFocus = <T extends HTMLInputElement | HTMLTextAreaElement>(
      event: React.FocusEvent<T>,
    ) => {
      setIsFocused(true);
      (onFocus as React.FocusEventHandler<T>)?.(event as React.FocusEvent<T>);
    };

    const handleBlur = <T extends HTMLInputElement | HTMLTextAreaElement>(
      event: React.FocusEvent<T>,
    ) => {
      setIsFocused(false);
      (onBlur as React.FocusEventHandler<T>)?.(event as React.FocusEvent<T>);
      if (error) setTriggerErrorShake((e) => !e);
    };

    const handleTextareaChange = React.useCallback(() => {
      if (
        isTextarea &&
        internalInputRef.current &&
        internalInputRef.current instanceof HTMLTextAreaElement
      ) {
        autoResizeTextarea(internalInputRef.current, {
          minRows: rows,
          maxRows,
        });
      }
    }, [rows, maxRows, isTextarea]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: only run on mount
    React.useEffect(() => {
      handleTextareaChange();
    }, []);

    const handleChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      handleValidate().then((isValid) => setIsValid(isValid));
      setHasValue(Boolean(event.target.value));
      onChange?.(event);
      handleTextareaChange();
    };

    const handleKeyUp = React.useCallback(() => {}, []);

    const handleValidate = React.useCallback(async () => {
      const emailValidator = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

      // If validate is explicitly false, don't validate
      if (validate === false) {
        return null;
      }

      // If validate is a custom function, use it
      if (typeof validate === "function") {
        return await validate(value);
      }

      // Email validation
      if (type === "email") {
        if (!value || value.trim() === "") return null; // Don't validate empty emails
        return emailValidator.test(value);
      }

      // For other input types with validate=true, consider non-empty values as valid
      if (validate === true) {
        return Boolean(value && value.trim() !== "");
      }

      // Default: no validation
      return null;
    }, [validate, value, type]);

    const [isValid, setIsValid] = React.useState<boolean | null>(null);

    // Run validation when value changes or component mounts
    React.useEffect(() => {
      if (validate !== undefined) {
        handleValidate().then((result) => setIsValid(result));
      }
    }, [validate, handleValidate]);

    const shouldFloat = labelOutside
      ? false
      : Boolean(isFocused || hasValue || prepend || append);
    const hasError =
      Boolean(
        error ||
          (maxLength !== undefined && value && value.length > maxLength) ||
          (minLength !== undefined && value && value.length < minLength),
      ) || isValid === false;
    const hasErrorText = typeof error === "string";

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    React.useEffect(() => {
      if (error) setTriggerErrorShake((e) => !e);
    }, [error]);

    // Number stepper functions
    const handleStepUp = () => {
      if (!isNumberType || disabled) return;

      const currentValue =
        typeof value === "number"
          ? value
          : Number.isNaN(Number(value))
            ? 0
            : Number(value);
      const step = "step" in props && props.step ? Number(props.step) : 1;
      const max =
        "max" in props && props.max !== undefined
          ? Number(props.max)
          : Number.MAX_SAFE_INTEGER;
      const newValue = Math.min(currentValue + step, max);

      // Create synthetic event for onChange
      if (onChange && internalInputRef.current) {
        const syntheticEvent = {
          target: { ...internalInputRef.current, value: newValue.toString() },
          currentTarget: internalInputRef.current,
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    };

    const handleStepDown = () => {
      if (!isNumberType || disabled) return;

      const currentValue =
        typeof value === "number"
          ? value
          : Number.isNaN(Number(value))
            ? 0
            : Number(value);
      const step = "step" in props && props.step ? Number(props.step) : 1;
      const min =
        "min" in props && props.min !== undefined
          ? Number(props.min)
          : Number.MIN_SAFE_INTEGER;
      const newValue = Math.max(currentValue - step, min);

      // Create synthetic event for onChange
      if (onChange && internalInputRef.current) {
        const syntheticEvent = {
          target: { ...internalInputRef.current, value: newValue.toString() },
          currentTarget: internalInputRef.current,
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    };

    const hasSuccess = React.useMemo(() => {
      return (Boolean(success) || isValid === true) && !hasError;
    }, [success, isValid, hasError]);
    const hasSuccessText = typeof success === "string";
    const descriptionText =
      (typeof error === "string" && error) ||
      (typeof success === "string" && success) ||
      (typeof helper === "string" && helper);

    // Password toggle logic (not applicable for textarea or number)
    const isPasswordType = type === "password" && !isTextarea && !isNumberType;
    const isEmailType = type === "email" && !isTextarea && !isNumberType;
    const effectiveInputType = isPasswordType && showPassword ? "text" : type;

    // Determine actual trailing icon - password toggle takes precedence, not applicable for textarea or number with stepper
    let actualTrailingIcon = trailingIcon;
    let actualOnTrailingIconClick = trailingIconProps?.onClick;
    const { label: tooltipLabel, ...restActualTrailingIconProps } =
      trailingIconProps || {};
    let actualTooltipLabel = tooltipLabel;

    const actualTrailingIconProps = restActualTrailingIconProps;

    if (isPasswordType) {
      actualTrailingIcon = showPassword ? "eye-strikethrough" : "eye";
      actualOnTrailingIconClick = () => setShowPassword(!showPassword);
      actualTooltipLabel = showPassword
        ? t("util:hide", { val: t("auth:password") })
        : t("util:show", { val: t("auth:password") });
    }

    if (isEmailType) {
      if (hasSuccess) actualTrailingIcon = "checkmark-circle-fill";
      actualTrailingIconProps.className = cn(
        "duration-500 ease-expressive-out",
        !trailingIcon && "opacity-0 scale-75",
        hasSuccess && "text-icon-success opacity-100 scale-100",
      );
    }

    // Don't show trailing icons for textarea or number inputs with stepper
    if (isTextarea) {
      actualTrailingIcon = undefined;
      actualOnTrailingIconClick = undefined;
    }

    const {
      input: inputClasses,
      inputWrapper: inputWrapperClasses,
      label: labelClasses,
      leadingIcon: leadingIconClasses,
      trailingIcon: trailingIconClasses,
      stepper: stepperClasses,
      stepperButton: stepperButtonClasses,
      stepperDivider: stepperDividerClasses,
      container: containerClasses,
      inputContainer: inputContainerClasses,
      helperText: helperTextClasses,
    } = inputVariants({
      size,
      hasError,
      hasSuccess,
      hasLeadingIcon: Boolean(leadingIcon) && !isTextarea, // No leading icons for textarea
      hasTrailingIcon: Boolean(actualTrailingIcon),
      hasStepper: isNumberType,
      isFloating: shouldFloat,
      hasLabel: !!label,
      isTextarea,
      trailingIconClickable: Boolean(actualOnTrailingIconClick),
      labelOutside,
    });

    // Combined ref handler that works with both callback refs and ref objects
    const combinedRef = React.useCallback(
      (node: HTMLInputElement | HTMLTextAreaElement | null) => {
        // Set our internal ref
        (
          internalInputRef as React.MutableRefObject<
            HTMLInputElement | HTMLTextAreaElement | null
          >
        ).current = node;

        // Handle the forwarded ref
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );
    const placeholderWidth = useMemo(() => {
      const fontSize = size === "md" ? "12px" : "16px";
      return getTextWidth(placeholder || "", `${fontSize} Nunito`);
    }, [placeholder, size]);

    const iconElement = actualTrailingIcon && !isTextarea && (
      <div
        role={actualOnTrailingIconClick ? "button" : undefined}
        tabIndex={actualOnTrailingIconClick ? 0 : undefined}
        aria-label={
          typeof actualTooltipLabel === "string"
            ? actualTooltipLabel
            : undefined
        }
        onKeyDown={(e) => {
          if (
            actualOnTrailingIconClick &&
            (e.key === "Enter" || e.key === " ")
          ) {
            e.preventDefault();
            actualOnTrailingIconClick?.(
              e as unknown as React.MouseEvent<HTMLDivElement>,
            );
          }
        }}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        onClick={actualOnTrailingIconClick}
        {...actualTrailingIconProps}
        className={trailingIconClasses({
          className: cn(
            trailingIconProps,
            classNames?.trailingIcon,
            actualTrailingIconProps.className,
          ),
        })}
      >
        <Icon
          name={actualTrailingIcon}
          className={cn("h-5 w-5 fill-current")}
          aria-hidden="true"
        />
      </div>
    );

    return (
      <div
        className={containerClasses({
          className: cn(classNames?.container),
        })}
        style={
          {
            "--scaled": size === "xl" ? 0.875 : 0.75,
          } as React.CSSProperties
        }
      >
        {/* Outside Label */}
        {labelOutside && label && (
          <label
            htmlFor={id || inputId}
            className={labelClasses({ className: classNames?.label })}
          >
            {label}
            {/* {required && (
              <span aria-hidden="true" className="ms-1">
                *
              </span>
            )} */}
          </label>
        )}

        <div
          className={inputContainerClasses({
            className: cn(classNames?.inputContainer),
          })}
          onMouseDown={(e) => {
            handleSetFocus(e);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleSetFocus(e);
            }
          }}
        >
          {/* Leading Icon - only for input, not textarea */}
          {leadingIcon && !isTextarea && (
            <div
              className={leadingIconClasses({
                className: classNames?.leadingIcon,
              })}
            >
              <Icon name={leadingIcon} className="size-5" aria-hidden="true" />
            </div>
          )}

          <div
            className={inputWrapperClasses({
              className: classNames?.inputWrapper,
            })}
          >
            {/* Prepend slot */}
            {prepend && <div className="contents">{prepend}</div>}

            {/* Input or Textarea */}
            {isTextarea ? (
              <textarea
                ref={combinedRef as React.Ref<HTMLTextAreaElement>}
                id={id || inputId}
                value={value}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyUp={handleKeyUp}
                disabled={disabled}
                required={required}
                placeholder={
                  labelOutside || shouldFloat || !label ? placeholder : ""
                }
                className={inputClasses({
                  className: cn(className, classNames?.input),
                })}
                aria-describedby={
                  descriptionText ? `${inputId}-description` : undefined
                }
                aria-invalid={hasError}
                aria-required={required}
                maxLength={maxLength}
                minLength={minLength}
                rows={rows}
                {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
              />
            ) : (
              <input
                ref={combinedRef as React.Ref<HTMLInputElement>}
                id={id || inputId}
                type={effectiveInputType}
                inputMode={
                  isNumberType
                    ? "inputMode" in props && props.inputMode
                      ? props.inputMode
                      : "decimal"
                    : undefined
                }
                pattern={
                  isNumberType
                    ? "pattern" in props && props.pattern
                      ? props.pattern
                      : "step" in props && props.step && props.step % 1 === 0
                        ? "[0-9]*"
                        : undefined
                    : undefined
                }
                value={value}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyUp={handleKeyUp}
                disabled={disabled}
                required={required}
                placeholder={
                  labelOutside || shouldFloat || !label ? placeholder : ""
                }
                className={inputClasses({
                  className: cn("w-auto", className, classNames?.input),
                })}
                aria-describedby={
                  descriptionText ? `${inputId}-description` : undefined
                }
                aria-invalid={hasError}
                aria-required={required}
                // maxLength={maxLength}
                // minLength={minLength}

                style={{
                  width: !placeholder ? "0px" : undefined,
                }}
                {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
              />
            )}
            {/* Append slot */}
            {append && <div className="contents">{append}</div>}
          </div>

          {/* Floating Label - only when label is inside */}
          {!labelOutside && label && (
            <label
              htmlFor={id || inputId}
              onClick={() => internalInputRef.current?.focus()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  internalInputRef.current?.focus();
                }
              }}
              className={labelClasses({
                className: classNames?.label,
              })}
            >
              <span>
                {label}
                {/* {required && (
                  <span aria-hidden="true" className="ms-1">
                    *
                  </span>
                )} */}
              </span>
            </label>
          )}

          {/* Number Stepper - only for number inputs */}
          {isNumberType && !actualTrailingIcon && (
            <div className={stepperClasses()}>
              <button
                type="button"
                className={stepperButtonClasses()}
                onClick={handleStepUp}
                disabled={
                  disabled ||
                  ("max" in props &&
                    typeof value === "number" &&
                    props.max !== undefined &&
                    value >= props.max)
                }
                aria-label="Increase value"
                tabIndex={-1}
              >
                <Icon
                  name="arrowhead-up-md"
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              </button>
              <div className={stepperDividerClasses()} />
              <button
                type="button"
                className={stepperButtonClasses()}
                onClick={handleStepDown}
                disabled={
                  disabled ||
                  ("min" in props &&
                    typeof value === "number" &&
                    props.min !== undefined &&
                    value <= props.min)
                }
                aria-label="Decrease value"
                tabIndex={-1}
              >
                <Icon
                  name="arrowhead-down-md"
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              </button>
            </div>
          )}
          {/* Trailing Icon - only for input, not textarea or number */}

          {actualTooltipLabel ? (
            <TooltipProvider primaryContent={actualTooltipLabel} sideOffset={0}>
              {iconElement}
            </TooltipProvider>
          ) : (
            iconElement
          )}
        </div>

        {(descriptionText ||
          maxLength !== undefined ||
          minLength !== undefined) && (
          <div className={"flex justify-between"}>
            <Shake trigger={triggerErrorShake}>
              {descriptionText && (
                <p
                  id={`${inputId}-description`}
                  className={helperTextClasses()}
                >
                  {descriptionText}
                </p>
              )}
            </Shake>
            <CharacterCounter
              value={value ?? ""}
              minLength={minLength}
              maxLength={maxLength}
              inputRef={internalInputRef}
            />
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";
